import { ICollidable } from './collision/types';
import { PhysicsObject } from './physics/physics-object';
import { Cat } from './cat';
import { Pit } from './pit';
import { DEBUG } from './debug';

export class Glass extends PhysicsObject {
	override readonly collisionType = 'glass';

	// Glass-specific
	private friction: number = 0.85;
	private lastAttackFrame: number = -1;

	// Sprite frames
	private frames: p5.Image[] = [];
	private currentFrame: number = 0;
	private frameCounter: number = 0;
	private frameSpeed: number = 4;
	private isLoaded: boolean = false;

	// Breaking state
	private isBroken: boolean = false;
	private isBreaking: boolean = false;
	private fallStartY: number | null = null;
	private static readonly BREAK_FALL_HEIGHT = 32;

	constructor(x: number, groundY: number) {
		super(x, groundY - 40 + 2, 40, 40, groundY);
	}

	async loadSprite(): Promise<void> {
		return new Promise((resolve) => {
			loadImage('./assets/models/glass.png', (img) => {
				const frameWidth = 40;
				const frameHeight = 40;
				const frameCount = 6;

				for (let i = 0; i < frameCount; i++) {
					const frame = createImage(frameWidth, frameHeight);
					frame.copy(
						img,
						i * frameWidth,
						0,
						frameWidth,
						frameHeight,
						0,
						0,
						frameWidth,
						frameHeight
					);
					this.frames.push(frame);
				}

				this.width = frameWidth;
				this.height = frameHeight;
				this.isLoaded = true;
				resolve();
			});
		});
	}

	protected override getGroundOffset(): number {
		return 2;
	}

	protected override onPitBottom(): void {
		this.startBreaking();
	}

	isActive(): boolean {
		return !this.isBroken;
	}

	push(forceX: number, forceY: number): void {
		this.vx += forceX;
		this.vy += forceY;
		// Track fall start when pushed up
		if (forceY < 0 && this.fallStartY === null) {
			this.fallStartY = this.y;
		}
	}

	onCollision(other: ICollidable): void {
		if (other instanceof Cat) {
			// If cat is attacking, apply push
			const currentAttackFrame = other.animationManager.currentFrame;
			if (other.isAttacking && currentAttackFrame === 0 && this.lastAttackFrame !== 0) {
				const pushDirection = other.animationManager.direction === 'east' ? 1 : -1;

				// Calculate force based on cat's momentum
				const catSpeed = Math.abs(other.vx);
				const runningThreshold = 2.1;
				const walkingThreshold = 0.5;

				console.log('Glass push - Cat velocity:', other.vx, '| Speed:', catSpeed);

				let baseForce: number;
				if (catSpeed > runningThreshold) {
					baseForce = 5.5 + Math.random() * 2.0;
				} else if (catSpeed > walkingThreshold) {
					baseForce = 2.0 + Math.random() * 1.0;
				} else {
					baseForce = 0.3 + Math.random() * 0.5;
				}

				console.log('Base force:', baseForce);

				const verticalPush = -0.5 - Math.random() * 0.5;
				this.push(pushDirection * baseForce, verticalPush);
				this.lastAttackFrame = 0;
			} else if (!other.isAttacking || currentAttackFrame !== 0) {
				this.lastAttackFrame = -1;
			}
		} else if (other.collisionType === 'pit') {
			this.handlePitCollision(other as Pit);
		}
	}

	private startBreaking(): void {
		if (!this.isBreaking && !this.isBroken) {
			this.isBreaking = true;
			this.currentFrame = 1; // Start from first breaking frame
			this.frameCounter = 0;
		}
	}

	update(): void {
		if (this.isBroken) return;

		// Track fall start when leaving ground
		if (!this.isGrounded && this.vy > 0 && this.fallStartY === null) {
			this.fallStartY = this.y;
		}

		// Check if was falling and just landed
		const wasInAir = !this.isGrounded;

		this.applyPhysics();

		// Check for landing after fall
		if (wasInAir && this.isGrounded && this.fallStartY !== null) {
			const fallHeight = this.y - this.fallStartY;
			if (fallHeight >= Glass.BREAK_FALL_HEIGHT) {
				this.startBreaking();
			}
			this.fallStartY = null;
		}

		// Update breaking animation
		if (this.isBreaking) {
			this.frameCounter++;
			if (this.frameCounter >= this.frameSpeed) {
				this.currentFrame++;
				this.frameCounter = 0;
				// Animation complete
				if (this.currentFrame >= this.frames.length) {
					this.isBroken = true;
					this.isBreaking = false;
				}
			}
			return; // Skip movement during breaking
		}

		// Friction only when grounded and not in pit
		if (this.isGrounded && !this.isInPit) {
			this.vx *= this.friction;
		}

		// Stop if very slow
		if (Math.abs(this.vx) < 0.1) {
			this.vx = 0;
		}

		// Screen bounds (only when not in pit)
		if (!this.isInPit) {
			this.x = constrain(this.x, 0, 1024 - this.width);
		}
	}

	draw(): void {
		if (!this.isLoaded || this.frames.length === 0) return;

		// Show last frame when broken (debris)
		const frameIndex = this.isBroken ? this.frames.length - 1 : this.currentFrame;
		const frame = this.frames[frameIndex];
		if (frame) {
			image(frame, this.x, this.y);
		}

		if (DEBUG.COLLISION) {
			noFill();
			stroke(255, 165, 0);
			strokeWeight(2);
			rect(this.x, this.y, this.width, this.height);
		}
	}

	break(): void {
		this.startBreaking();
	}

	reset(x: number, groundY: number): void {
		this.x = x;
		this.groundY = groundY;
		this.y = groundY - this.height + 2;
		this.vx = 0;
		this.vy = 0;
		this.isBroken = false;
		this.isBreaking = false;
		this.currentFrame = 0;
		this.frameCounter = 0;
		this.fallStartY = null;
		this.resetPitState();
	}
}
